/**
 * Equipe de um projeto, derivada das tarefas — como o progresso, não há coluna no banco.
 *
 * O projeto tem um único `responsavel_id`, que é quem conduz a iniciativa (o gestor). Quem
 * executa aparece nas tarefas vinculadas, e era invisível no card e no cabeçalho: o "Módulo Inbox
 * Omnichannel" mostrava só "Felipe Braz" mesmo com 15 tarefas divididas entre Benevides e Victor.
 *
 * Derivar em vez de criar tabela de membros: vínculo de pessoa a projeto já existe — é a tarefa.
 * Uma segunda lista precisaria ser mantida à mão e divergiria de quem está de fato trabalhando.
 */
import type { TarefaProgresso } from './progresso.ts';
import { situacaoPrazo } from './tarefasProjeto.ts';

export interface TarefaEquipe extends TarefaProgresso {
  responsavel_id?: string | null;
}

export interface MembroEquipe {
  userId: string;
  /** 'gestor' é o responsavel_id do projeto; 'participante' vem das tarefas. */
  papel: 'gestor' | 'participante';
  /** Tarefas do projeto atribuídas à pessoa (exclui canceladas/rejeitadas). */
  tarefas: number;
  concluidas: number;
  /** Não concluídas com vencimento já passado — mostra com quem o atraso está. */
  atrasadas: number;
}

const CONCLUIDOS = new Set(['concluido']);
const DESCARTADOS = new Set(['rejeitado', 'cancelado']);

const situacao = (t: TarefaEquipe): string => t.solicitacao_status || t.status || '';

interface Entradas {
  /** responsavel_id do projeto. Entra sempre, mesmo sem tarefa nenhuma no nome dele. */
  gestorId?: string | null;
  /** Tarefas do projeto e as subtarefas delas — subtarefa tem dono próprio e conta. */
  tarefas: TarefaEquipe[];
  /** Referência para "atrasada"; parâmetro só para os testes fixarem a data. */
  hoje?: Date;
}

/**
 * Gestor primeiro; participantes ordenados por volume de trabalho (desempate pelo id, só para a
 * ordem ser estável entre renders — o nome não é conhecido aqui).
 */
export const calcularEquipe = ({ gestorId, tarefas, hoje = new Date() }: Entradas): MembroEquipe[] => {
  const contagem = new Map<string, { tarefas: number; concluidas: number; atrasadas: number }>();

  tarefas.forEach(t => {
    // Descartada não é trabalho de ninguém: quem só tem tarefa rejeitada não vira participante.
    if (DESCARTADOS.has(situacao(t))) return;
    const uid = t.responsavel_id;
    if (!uid) return; // Solicitação na fila, ainda sem dono
    const atual = contagem.get(uid) || { tarefas: 0, concluidas: 0, atrasadas: 0 };
    atual.tarefas += 1;
    if (CONCLUIDOS.has(situacao(t))) atual.concluidas += 1;
    if (situacaoPrazo(t, hoje) === 'atrasada') atual.atrasadas += 1;
    contagem.set(uid, atual);
  });

  const participantes: MembroEquipe[] = Array.from(contagem.entries())
    .filter(([uid]) => uid !== gestorId)
    .map(([userId, c]) => ({ userId, papel: 'participante' as const, ...c }))
    .sort((a, b) => b.tarefas - a.tarefas || a.userId.localeCompare(b.userId));

  if (!gestorId) return participantes;

  // O gestor também executa tarefas: o papel de gestor prevalece, mas a contagem dele é a real.
  const doGestor = contagem.get(gestorId) || { tarefas: 0, concluidas: 0, atrasadas: 0 };
  return [{ userId: gestorId, papel: 'gestor', ...doGestor }, ...participantes];
};
